<template>
  <div v-if="sceneTree.activeScene" class="scene-background-panel" ref="sceneStyleContainer">
    <button
      ref="sceneStyleButton"
      type="button"
      class="button-icon ph-1 scene-background-panel__button"
      @click="toggleStylePanel"
    >
      <lucide-icon name="palette" />
    </button>

    <floating-element
      class="scene-background-panel__panel"
      :visible="stylePanel.visible"
      :x="stylePanel.x"
      :y="stylePanel.y"
      :width="stylePanel.width"
      :min-width="60"
      :min-height="40"
      transition-name="transition-pop-in-up"
      x-alignment="center"
      y-alignment="bottom"
      :non-closing-elements="sceneStyleButton ? [sceneStyleButton] : []"
      @click:outside="onClickOutsideStylePanel"
      @click="onBackgroundPanelClick($event)"
    >
      <template #default>
        <div class="column gap-4">
          <div>
            <!-- Template -->
            <div class="label">{{ $t("objectNames.template") }}</div>
            <button-item
              class="scene-background-panel__template-button"
              ref="sceneTemplateButton"
              :icons="template ? ['x'] : []"
              @click:x="deactivateTemplate"
              @click="toggleTemplatePanel"
            >
              <lucide-icon name="layout-template" />
              {{ template ? template.title : $t("actions.chooseX", [$t("objectNames.template")]) }}
            </button-item>
          </div>

          <div>
            <input-number
              :value="sceneTree.activeScene.fontSize"
              :min="8"
              :max="60"
              :label="$t('labels.fontSize')"
              @update:value="sceneStore.updateKey(sceneTree.activeScene.id, 'fontSize', $event)"
            />
          </div>

          <div>
            <!-- Background color -->
            <div class="label">
              {{ $t("labels.backgroundColor") }}
            </div>
            <color-picker
              :value="sceneTree.activeScene.backgroundColor"
              :transparent="false"
              :null-allowed="false"
              @update:value="sceneStore.updateKey(sceneTree.activeScene.id, 'backgroundColor', $event!)"
            />
          </div>

          <input-image
            :id="backgroundImageId ?? undefined"
            :labeltext="$t('labels.backgroundImage')"
            :max-width="3840"
            :max-height="2160"
            :compression="0.95"
            :control-object-fit="true"
            :control-repeat="true"
            :object-fit-values="['contain', 'cover', 'none']"
            @update="updateImage"
            @delete="deleteImage"
          />

          <input-switch :value="sceneTree.activeScene.footerAnchored" @update:value="sceneStore.updateKey(sceneTree.activeScene.id, 'footerAnchored', $event)">
            {{ $t("labels.footerAnchored") }}
          </input-switch>
        </div>
      </template>
    </floating-element>

    <floating-element
      class="scene-template-panel__panel"
      :visible="templatePanel.visible"
      :x="templatePanel.x"
      :y="templatePanel.y"
      :width="templatePanel.width"
      :min-width="60"
      :min-height="40"
      transition-name="transition-pop-in-up"
      x-alignment="center"
      y-alignment="bottom"
      :non-closing-elements="sceneTemplateButton?.$el ? [sceneTemplateButton.$el] : []"
      @click:outside="onClickOutsideTemplatePanel"
    >
      <template #default>
        <scene-template-panel-menu />
      </template>
    </floating-element>
  </div>
</template>

<script setup lang="ts">
import { reactive, useTemplateRef, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import LucideIcon from '@core/ui/LucideIcon.vue';
import FloatingElement from '@core/ui/FloatingElement.vue';

import ColorPicker from '@core/input/ColorPicker.vue';
import InputSwitch from '@core/input/InputSwitch.vue';
import InputNumber from '@core/input/InputNumber.vue';
import ButtonItem from '@core/input/ButtonItem.vue';
import InputImage from '../image/InputImage.vue';
import SceneTemplatePanelMenu from './SceneTemplatePanelMenu.vue';

import { useSceneStore } from './scenes.store';
import { useSceneTree } from './sceneTree';
import { useTemplateStore } from '../template/templates.store';
import { showConfirm } from '@core/ui/alert/alert';

const sceneStyleButton = useTemplateRef('sceneStyleButton');

const sceneTree = useSceneTree();
const sceneStore = useSceneStore();


// ==================
// IMAGE MENU

const backgroundImageId = computed(() => {
  return sceneTree.activeScene.backgroundImageId;
});

function updateImage(image: { id: number }) {
  sceneStore.updateKey(sceneTree.activeScene.id, "backgroundImageId", image.id);
}
function deleteImage() {
  sceneStore.updateKey(sceneTree.activeScene.id, "backgroundImageId", null);
}

// =======================
// SWITCHER PANEL

const stylePanel = reactive({
  visible: false,
  x: 100,
  y: 100,
  width: 275,
  height: 40
});

function toggleStylePanel() {
  stylePanel.visible = !stylePanel.visible;
  if (stylePanel.visible) {
    // Position the panel below the button
    const buttonRect = sceneStyleButton.value?.getBoundingClientRect();
    if (buttonRect) {
      stylePanel.x = buttonRect.left + buttonRect.width / 2;
      stylePanel.y = buttonRect.top; // 5px gap
    }
  }
}

function onClickOutsideStylePanel(event: MouseEvent) {
  const path = event.composedPath();
  // Prevent drag events from closing the panel 
  if (path.length > 0 && (path[0] as HTMLElement).id == "app") {
    return;
  }
  stylePanel.visible = false;
}

// ==================
// TEMPLATE PANEL
  
const templateStore = useTemplateStore();
const sceneTemplateButton = useTemplateRef('sceneTemplateButton');
const { t } = useI18n();

const template = computed(() => {
  return templateStore.template;
});

const templatePanel = reactive({
  visible: false,
  x: 100,
  y: 100,
  width: 275,
  height: 40
});

function toggleTemplatePanel() {
  templatePanel.visible = !templatePanel.visible;
  if (templatePanel.visible) {
    // Position the panel below the button
    const buttonRect = sceneTemplateButton.value?.getBoundingClientRect();
    if (buttonRect) {
      templatePanel.x = buttonRect.left + buttonRect.width / 2;
      templatePanel.y = buttonRect.top; // 5px gap
    }
  }
}

function onBackgroundPanelClick(event: MouseEvent) {
  const path = event.composedPath();
  const onTemplateButton = path.some(node => (node as HTMLElement).classList?.contains("scene-background-panel__template-button"));
  if (!onTemplateButton) {
    templatePanel.visible = false;
  }
}

function onClickOutsideTemplatePanel(event: MouseEvent) {
  const path = event.composedPath();
  console.log("click outside template panel", { path, event });
  // Prevent drag events from closing the panel 
  if (path.length > 0 && (path[0] as HTMLElement).id == "app") {
    return;
  }
  templatePanel.visible = false;
}

async function deactivateTemplate() {
  if (await showConfirm(t("questions.confirmDeactivatingTemplate"))) {
    templateStore.deactivate();
  }
}
</script>

<style lang="scss">
.scene-background-panel {
  &__button {
    flex-direction: row;
    white-space: nowrap;
    gap: 0.5rem;
  }
  &__buttontext {
    font-size: 1rem;
    font-weight: 400;
    overflow: hidden;
    max-width: 12rem;
    text-overflow: ellipsis;
  }

  &__panel {
    padding: 1rem;
    align-items: center;
    overflow: hidden;
    background-color: var(--color-background-deep);
    border-radius: 0.5rem;
    box-shadow: 0px 1px 7px var(--color-shadow-3);
  }

  &__list-item {
    background-color: var(--color-background-deep);
    display: flex;
    width: 100%;
    text-align: left;

    overflow: hidden;
    text-overflow: ellipsis;
    min-height: 2em;

    &:not(:disabled):hover,
    &:not(:disabled):focus-visible,
    &:not(:disabled):focus-within {
      background-color: var(--color-background);
    }
    &:disabled {
      color: var(--color-text-lighter);
    }
    &.-selected {   
      background-color: var(--color-primary-light);
      font-weight: 600;
      color: var(--color-text);

      &:hover {
        background-color: var(--color-primary-light);
      }
    }
  }
  &__list-item-button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.25rem 1rem;
  }

  &__action-icon {
    opacity: 0;
    font-size: 1.1rem;
    pointer-events: auto;
    :hover > &,
    :focus-within > & {
      opacity: 1;
    }

    .lucide-icon {
      pointer-events: none;
    }
  }
}
</style>